import { useCallback, useMemo, useState } from "react"

import { useUrlQuery } from "../../../../hooks/useUrlQuery"

const hideParam = "hide"

interface UseHideColumnsRes {
  hiddenColumns: Set<string>
  toggleColumnHideShow: (key: string) => void
  clearHiddenColumns: () => void
  hideAllColumns: (keys: string[]) => void
}

export function useHideColumns({
  persistInUrl = true,
}: { persistInUrl?: boolean } = {}): UseHideColumnsRes {
  const urlRes = useUrlHideColumns()
  const localRes = useLocalHideColumns()
  return persistInUrl ? urlRes : localRes
}

function useUrlHideColumns(): UseHideColumnsRes {
  const { query: urlQuery, pushQuery } = useUrlQuery()
  const raw = urlQuery[hideParam]

  const hiddenColumns = useMemo(() => {
    if (raw === undefined) return new Set<string>()
    return new Set(Array.isArray(raw) ? raw : [raw])
  }, [raw])

  const toggleColumnHideShow = useCallback(
    (key: string) => {
      const next = new Set(hiddenColumns)
      next.has(key) ? next.delete(key) : next.add(key)

      const query = { ...urlQuery }
      if (next.size > 0) {
        query[hideParam] = Array.from(next)
      } else {
        delete query[hideParam]
      }

      pushQuery(query)
    },
    [hiddenColumns, urlQuery, pushQuery]
  )

  const clearHiddenColumns = useCallback(() => {
    const query = { ...urlQuery }
    delete query[hideParam]
    pushQuery(query)
  }, [urlQuery, pushQuery])

  const hideAllColumns = useCallback(
    (next: string[]) => {
      const columnsToHide = Array.from(new Set([...hiddenColumns, ...next]))
      const query = { ...urlQuery }
      if (columnsToHide.length > 0) {
        query[hideParam] = columnsToHide
      } else {
        delete query[hideParam]
      }
      pushQuery(query)
    },
    [hiddenColumns, urlQuery, pushQuery]
  )

  return {
    hiddenColumns,
    toggleColumnHideShow,
    clearHiddenColumns,
    hideAllColumns,
  }
}

function useLocalHideColumns(): UseHideColumnsRes {
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set())

  const toggleColumnHideShow = useCallback((key: string) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }, [])

  const clearHiddenColumns = useCallback(() => setHiddenColumns(new Set()), [])

  const hideAllColumns = useCallback((next: string[]) => {
    setHiddenColumns((prev) => new Set([...prev, ...next]))
  }, [])

  return {
    hiddenColumns,
    toggleColumnHideShow,
    clearHiddenColumns,
    hideAllColumns,
  }
}
